import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCheckCircle, FaClock, FaCircle, FaTimesCircle,
  FaFileAlt, FaRupeeSign, FaSync
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { getMyApplications } from '../utils/api';
import './MyApplications.css';

const statusConfig = {
  'Pending': {
    color: '#f59e0b',
    bg: '#fffbeb',
    border: '#fcd34d',
    icon: FaClock,
    label: 'Pending',
    step: 1,
  },
  'Under Review': {
    color: '#3b82f6',
    bg: '#eff6ff',
    border: '#93c5fd',
    icon: FaSync,
    label: 'Under Review',
    step: 2,
  },
  'Approved': {
    color: '#10b981',
    bg: '#ecfdf5',
    border: '#6ee7b7',
    icon: FaCheckCircle,
    label: 'Approved',
    step: 3,
  },
  'Rejected': {
    color: '#ef4444',
    bg: '#fef2f2',
    border: '#fca5a5',
    icon: FaTimesCircle,
    label: 'Rejected',
    step: 3,
  },
};

const stages = ['Pending', 'Under Review', 'Approved / Rejected'];

const MyApplications = () => {
  const { user, token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchApplications = useCallback(async () => {
    try {
      const data = await getMyApplications(token);
      setApplications(data);
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchApplications();
    const refreshInterval = window.setInterval(fetchApplications, 60000);
    return () => window.clearInterval(refreshInterval);
  }, [fetchApplications]);

  if (isLoading) {
    return (
      <div className="my-apps-page">
        <div className="my-apps-container">
          <div className="loading-state">
            <FaClock className="spin-icon" />
            <p>Loading your applications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-apps-page">
      <div className="my-apps-container">

        {/* Header */}
        <div className="my-apps-header">
          <div>
            <h1>My Applications</h1>
            <p>Welcome, <strong>{user?.name}</strong> — track your loan status below</p>
          </div>
          <button onClick={fetchApplications} className="refresh-btn">
            <FaSync style={{ marginRight: 6 }} /> Refresh
          </button>
        </div>

        {/* No Applications */}
        {applications.length === 0 ? (
          <div className="no-apps-state">
            <FaFileAlt className="no-apps-icon" />
            <h3>No applications yet</h3>
            <p>You haven't submitted any loan applications.</p>
            <Link to="/apply" className="apply-now-link">Apply for a Loan →</Link>
          </div>
        ) : (
          <div className="apps-list">
            {applications.map((app) => {
              const cfg = statusConfig[app.status] || statusConfig['Pending'];
              const Icon = cfg.icon;
              const stepIndex = cfg.step - 1;

              return (
                <div key={app.id} className="app-card">
                  {/* Card Header */}
                  <div className="app-card-header">
                    <div className="app-id-badge">Application #{app.id}</div>
                    <div
                      className="app-status-badge"
                      style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
                    >
                      <Icon style={{ marginRight: 5 }} />
                      {cfg.label}
                    </div>
                  </div>

                  {/* Loan Info */}
                  <div className="app-info-row">
                    <div className="app-info-item">
                      <span className="info-label">Loan Type</span>
                      <span className="info-value">{app.loan_type}</span>
                    </div>
                    <div className="app-info-item">
                      <span className="info-label">Amount</span>
                      <span className="info-value">
                        <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                        {parseInt(app.loan_amount || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="app-info-item">
                      <span className="info-label">Applied On</span>
                      <span className="info-value">
                        {app.application_date
                          ? new Date(app.application_date).toLocaleDateString('en-IN')
                          : 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Progress Timeline */}
                  <div className="progress-timeline">
                    {stages.map((stage, idx) => {
                      const isCompleted = idx < stepIndex;
                      const isActive = idx === stepIndex && app.status !== 'Rejected';
                      const isRejected = app.status === 'Rejected' && idx === stepIndex;

                      return (
                        <div key={stage} className="timeline-stage">
                          <div
                            className={`timeline-dot ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isRejected ? 'rejected' : ''}`}
                            style={isActive || isCompleted ? { background: cfg.color, borderColor: cfg.color } : {}}
                          >
                            {isCompleted ? '✓' : isRejected ? '✕' : idx + 1}
                          </div>
                          <span
                            className="timeline-label"
                            style={(isActive || isCompleted) ? { color: cfg.color, fontWeight: 600 } : {}}
                          >
                            {stage}
                          </span>
                          {idx < stages.length - 1 && (
                            <div className={`timeline-line ${isCompleted ? 'completed' : ''}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Status Message */}
                  {app.status_message && (
                    <div
                      className="app-status-message"
                      style={{ background: cfg.bg, borderColor: cfg.border, color: cfg.color }}
                    >
                      <Icon style={{ marginRight: 8, flexShrink: 0 }} />
                      {app.status_message}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Apply Again Link */}
        {applications.length > 0 && (
          <div className="apply-another">
            <Link to="/apply">+ Apply for another loan</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplications;
